import { getLocalizedDateMask } from "$root/localization/formatters";
import { useLocaleFormatters } from "$root/localization/hooks";
import { Calendar } from "@mdotm/shadcn/legacy/components";
import { Button, TextInput } from "@mdotm/shadcn/mdotm/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { Popover, PopoverContent, PopoverTrigger } from "@mdotm/shadcn/ui";
import { useState } from "react";
import { parseTypedDay } from "./typedDay";

/**
 * A popover that picks one day: a text field over a calendar, with a footer that commits.
 *
 * It knows nothing about what the day means to its caller. The selectable window, the rule that
 * settles what is in the field, every string and every `data-qualifier` arrive as props, so the two
 * features that use it — the portfolio history view's "as of" and the composition editor's reference
 * date — stay wholly in their own folders.
 *
 * Every day in range is selectable, weekends included.
 *
 * A date can be typed as well as picked, in the locale's short format and with the same masking every
 * other date field in the app uses. Both routes stage the same draft, and the field is settled by
 * `accept` — the caller's own rule, the same one that vets its URL param, so what can be typed and
 * what can be linked to are one set rather than two that drift.
 *
 * Picking or typing a day stages it; the commit button is what puts it into force. The commit can
 * cost the caller a multi-minute backend generation, so it is not something a stray click on a
 * calendar cell should be able to trigger.
 */
export function DatePickerPopover(props: {
	/** The day the caller is on; null where the caller's feature has an "unset" state. */
	value: string | null;
	/** Selectable window, `YYYY-MM-DD`, as the `Calendar` takes them. */
	min?: string;
	max: string;
	/** The caller's validity rule: the accepted day, or null. */
	accept(day: string): string | null;
	labels: {
		/** The popover's accessible name. */
		dialog: string;
		/** The text field's accessible name. */
		typeADate: string;
		/** The commit button, on a draft that differs from `value`. */
		commit: string;
		/** The commit button when it would re-fire on `value` itself; needs `allowRecommit`. */
		recommit?: string;
	};
	/** Called with the bounds already formatted for display. */
	rangeHint(bounds: { from?: string; to: string }): string;
	/** Prefix: the field, its hint and the commit button hang off it. */
	qualifier: string;
	onCommit(day: string): void;
	/** Lets the commit button fire on a draft equal to `value`, reading `labels.recommit`. */
	allowRecommit?: boolean;
	/**
	 * Rendered at the footer's leading edge. It is handed `close` because a footer control that acts on
	 * the page has the same reason to dismiss the popover as the commit button does.
	 */
	secondaryAction?(close: () => void): React.ReactNode;
	/** Rendered as the popover trigger (`asChild`), so it must be able to carry button props and a ref. */
	children: React.JSX.Element;
}): React.JSX.Element {
	const { value, min, max, accept, labels, rangeHint, qualifier, onCommit, allowRecommit, secondaryAction, children } =
		props;
	const [open, setOpen] = useState(false);
	// Picking a day only stages it; the commit button is what puts it into force. Asking for a date the
	// backend has not computed yet starts a generation that can take minutes, so it is not something a
	// stray click on a calendar cell should be able to trigger.
	const [draft, setDraft] = useState<string | null>(value);
	const { formatDate, formatDateForInput, locale } = useLocaleFormatters();
	const dateMask = getLocalizedDateMask(locale);
	// What is in the text field, in the user's short format. Kept beside `draft` rather than derived
	// from it because half of a date is a real thing to be holding, and no `YYYY-MM-DD` can hold it.
	const [typed, setTyped] = useState(() => formatDateForInput(value));

	// Reopening the calendar starts from the date the caller is actually on, discarding a staged pick the
	// user walked away from rather than presenting it as if it were in force.
	const resetDraftAndSetOpen = (nextOpen: boolean) => {
		if (nextOpen) {
			setDraft(value);
			setTyped(formatDateForInput(value));
		}
		setOpen(nextOpen);
	};

	// The field and the staged draft move together in both directions: a picked day fills the field, and
	// a typed day stages itself. `draft` therefore always says exactly what the field says — including
	// "nothing usable yet", so the commit button cannot fire on a date the user has since typed over.
	const stageFromCalendar = (date: string | null) => {
		setDraft(date);
		setTyped(formatDateForInput(date));
	};
	// The text is stored exactly as typed. Reformatting it here — putting the separators in as the digits
	// arrive — is what a masked field usually does, and it costs the caret its place on every keystroke:
	// the value React writes back differs from the one the browser had, so the cursor lands at the end.
	// Worse, it reflows a date the user is only editing: backspace over the month in `08/12/2026` and
	// every digit after the gap shifts up a field, leaving `01/22/026`. While the user is typing, what
	// they typed is the authority; the separators go in on blur, once they have stopped.
	const stageFromTyping = (text: string) => {
		setTyped(text);
		const read = parseTypedDay(text, { locale, accept });
		setDraft(read.kind === "accepted" ? read.date : null);
	};

	// What the field currently amounts to, read at render rather than stored, so there is one answer
	// rather than a copy of it that can fall behind.
	const typedDate = parseTypedDay(typed, { locale, accept });

	// Blur is where the field is tidied into the canonical form — which is also what turns the separators
	// the user did not bother typing into the ones they meant. An entry that does not parse is left
	// alone: it is still theirs to fix, and rewriting it would take the fixing away.
	const canonicaliseTyped = () => {
		if (typedDate.kind === "accepted") {
			setTyped(formatDateForInput(typedDate.date));
		}
	};

	// Committing the value the caller is already on is something it has to ask for: normally there is
	// nothing to do, and the button says so by staying disabled.
	const isRecommit = allowRecommit === true && draft != null && draft === value;

	// One action behind both the button and Enter, so typing a date and pressing Enter cannot end up
	// meaning something other than typing it and clicking the button.
	const canCommit = draft != null && (draft !== value || isRecommit);
	const commit = () => {
		if (draft != null) {
			onCommit(draft);
		}
		setOpen(false);
	};

	return (
		<Popover modal={false} open={open} onOpenChange={resetDraftAndSetOpen}>
			<PopoverTrigger asChild>{children}</PopoverTrigger>
			<PopoverContent align="start" sideOffset={4} role="dialog" aria-label={labels.dialog} className="p-0">
				<div className="border-b px-3 py-2" style={{ borderColor: themeCSSVars.palette_N100 }}>
					<TextInput
						type="text"
						size="x-small"
						placeholder={dateMask}
						value={typed}
						aria-label={labels.typeADate}
						aria-invalid={typedDate.kind === "rejected"}
						onChangeText={stageFromTyping}
						onBlur={canonicaliseTyped}
						onKeyDown={(event) => {
							if (event.key === "Enter" && canCommit) {
								commit();
							}
						}}
						data-qualifier={`${qualifier}/DateInput`}
					/>
					{typedDate.kind === "rejected" && (
						// `w-0 min-w-full` keeps this sentence out of the popover's width: a popover sizes to its
						// content, so a hint on one line made the whole thing jump from calendar-wide to
						// sentence-wide the moment it appeared. At width zero it contributes nothing to that
						// measurement, and `min-w-full` then gives it the width the calendar settled on, to wrap
						// inside. A max-width would have worked too, in the sense that a guessed number works
						// until the calendar or the font changes.
						<p
							className="mt-1 w-0 min-w-full text-xs"
							style={{ color: themeCSSVars.palette_D500 }}
							data-qualifier={`${qualifier}/DateInput/Hint`}
						>
							{rangeHint({ from: min ? formatDate(min) : undefined, to: formatDate(max) })}
						</p>
					)}
				</div>
				<Calendar value={draft} min={min} max={max} onChange={stageFromCalendar} />
				<div
					className={`flex items-center border-t px-3 py-2 ${
						secondaryAction ? "justify-between gap-4" : "justify-end"
					}`}
					style={{ borderColor: themeCSSVars.palette_N100 }}
				>
					{secondaryAction?.(() => setOpen(false))}
					<Button
						size="x-small"
						palette="primary"
						disabled={!canCommit}
						onClick={commit}
						data-qualifier={`${qualifier}/Apply`}
					>
						{isRecommit && labels.recommit ? labels.recommit : labels.commit}
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	);
}
